import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { PlusIcon, XIcon } from '@phosphor-icons/react';
import { ReactNode } from 'react';

const tagVariants = cva(
    'inline-flex w-max max-w-full shrink-0 flex-row items-center text-grey-200',
    {
        variants: {
            variant: {
                base: 'bg-grey-600 border border-grey-500',
                raised: 'bg-grey-550 border border-grey-500',
                add: 'border border-grey-500 bg-transparent',
                outline: 'border border-grey-500 bg-transparent',
                plain: 'border-0 bg-transparent',
            },
            size: {
                sm: 'gap-1 h-[22px] py-0.5 px-1.5 rounded-[4px] text-xs font-normal leading-[18px]',
                md: 'gap-2 h-8 py-1 px-2 rounded-md text-xs font-normal leading-[18px]',
            },
            selected: {
                true: 'border-transparent bg-grey-50 text-black-100',
                false: '',
            },
            clickable: {
                true: 'cursor-pointer select-none transition-colors',
                false: '',
            },
        },
        compoundVariants: [
            { clickable: true, selected: false, class: 'hover:bg-grey-550' },
        ],
        defaultVariants: {
            variant: 'base',
            size: 'sm',
            clickable: false,
            selected: false,
        },
    },
);

export type TagSize = 'sm' | 'md';

export interface TagProps {
    variant?: 'base' | 'add' | 'outline' | 'plain' | 'raised';
    size?: TagSize;
    selected?: boolean;
    children?: ReactNode;
    className?: string;
    leadingIcon?: ReactNode;
    trailingIcon?: ReactNode;
    onRemove?: () => void;
    onClick?: () => void;
    'data-testid'?: string;
}

const ICON_SLOT_CLASSES: Record<TagSize, string> = {
    sm: 'h-[18px] w-3 [&>svg]:size-3',
    md: 'h-6 w-4 [&>svg]:size-4',
};

const TRAILING_SLOT_CLASSES: Record<TagSize, string> = {
    sm: 'ml-1 h-[18px] [&>svg]:size-3',
    md: 'h-6 [&>svg]:size-4',
};

const REMOVE_BUTTON_CLASSES: Record<TagSize, string> = {
    sm: 'ml-1 h-[18px] w-3',
    md: 'h-6 w-4',
};

const REMOVE_ICON_CLASSES: Record<TagSize, string> = {
    sm: 'size-3',
    md: 'size-4',
};

const ADD_ICON_CLASSES: Record<TagSize, string> = {
    sm: 'size-3',
    md: 'size-4',
};

export function Tag({
    children,
    variant = 'base',
    size = 'sm',
    selected,
    className,
    leadingIcon,
    trailingIcon,
    onRemove,
    onClick,
    ...dataAttributes
}: TagProps) {
    const isAdd = variant === 'add';
    const slotColorClass = selected ? 'text-black-100' : 'text-grey-200';

    const content = (
        <>
            {isAdd ? (
                <span
                    className={cn(
                        'flex shrink-0 items-center justify-center',
                        ICON_SLOT_CLASSES[size],
                    )}
                >
                    <PlusIcon
                        className={cn(
                            'shrink-0',
                            slotColorClass,
                            ADD_ICON_CLASSES[size],
                        )}
                    />
                </span>
            ) : (
                leadingIcon && (
                    <span
                        className={cn(
                            'flex shrink-0 items-center justify-center [&>svg]:shrink-0',
                            slotColorClass,
                            '[&>svg]:text-current',
                            ICON_SLOT_CLASSES[size],
                        )}
                    >
                        {leadingIcon}
                    </span>
                )
            )}
            <span className="min-w-0 max-w-full truncate text-left">
                {isAdd && children === undefined ? 'Add' : children}
            </span>
            {!isAdd && trailingIcon && (
                <span
                    className={cn(
                        'flex shrink-0 items-center justify-center [&>svg]:shrink-0',
                        slotColorClass,
                        TRAILING_SLOT_CLASSES[size],
                    )}
                >
                    {trailingIcon}
                </span>
            )}
            {!isAdd && onRemove && (
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        onRemove();
                    }}
                    className={cn(
                        'flex shrink-0 items-center justify-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-grey-400',
                        slotColorClass,
                        REMOVE_BUTTON_CLASSES[size],
                    )}
                    aria-label="Remove tag"
                >
                    <XIcon
                        className={cn('shrink-0', REMOVE_ICON_CLASSES[size])}
                    />
                </button>
            )}
        </>
    );

    if (isAdd && onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                className={cn(
                    tagVariants({ variant, size, selected, clickable: true }),
                    className,
                )}
                aria-pressed={selected}
                {...dataAttributes}
            >
                {content}
            </button>
        );
    }

    if (!isAdd && onClick) {
        return (
            <div
                role="button"
                tabIndex={0}
                onClick={onClick}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onClick();
                    }
                }}
                className={cn(
                    tagVariants({ variant, size, selected, clickable: true }),
                    className,
                )}
                aria-pressed={selected}
                {...dataAttributes}
            >
                {content}
            </div>
        );
    }

    return (
        <span
            className={cn(tagVariants({ variant, size, selected }), className)}
            {...dataAttributes}
        >
            {content}
        </span>
    );
}
